import React, { useEffect, useState } from 'react';
import { Card, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Collapse, IconButton, Typography, Box, Button, Tooltip } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import useApi from '../../../shared/hooks/useApi';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';

const AfrrReservation = ({ colors, setLoading, triggerAlert }) => {
    const { apiGet } = useApi();
    const [afrrReservations, setAfrrReservations] = useState([]);
    const [afrrProposals, setAfrrProposals] = useState([]);
    const [mergedData, setMergedData] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});
    const [dateFrom, setDateFrom] = useState(dayjs().subtract(7, 'day'));
    const [dateTo, setDateTo] = useState(dayjs());
    // Debug helpers
    const debugLog = (...args) => console.log('[AFRR DEBUG 24-031]', ...args);
   
    useEffect(() => {
        setLoading(true);
        
        const getDataRoute = `/esid-24-031/get-afrr-reservations-by-date?from=${dateFrom.format('YYYY-MM-DD')}&to=${dateTo.format('YYYY-MM-DD')}`;
        const getAfrrReservations = async () => {
            try {
                const data = await apiGet("UX_HTTP_API", getDataRoute);
                
                const flattenedReservations = data.map((item, index) => ({
                    id: index + 1, // Assign an ID for each main item
                    date: item.date, // Example date, adjust as needed
                    volume: item.afrr_reservations.reduce((acc, curr) => acc + parseFloat(curr.afrr_down_capacity_reserved), 0), // Sum of down capacities
                    price: 'N/A', // Placeholder, adjust as needed
                    details: item.afrr_reservations // Store the detailed reservations
                }));
               
                setAfrrReservations(flattenedReservations);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setAfrrReservations([]);
                setLoading(false);
            }
        };
        const getDataProposalRoute = `/esid-24-031/get-afrr-proposals-by-date?from=${dateFrom.format('YYYY-MM-DD')}&to=${dateTo.format('YYYY-MM-DD')}`;
        const getAfrrProposals = async () => {
            try {
                const data = await apiGet("UX_HTTP_API", getDataProposalRoute);
                const flattenedProposals = data.map((item, index) => ({
                    id: index + 1, // Assign an ID for each main item
                    date: item.date, // Example date, adjust as needed
                    volume: item.afrr_reservations["AFRR-PROPOSAL"]?.reduce((acc, curr) => acc + parseFloat(curr.capacity_volume_down), 0) || 0, // Sum of down capacities
                    price: 'N/A', // Placeholder, adjust as needed
                    details: item.afrr_reservations["AFRR-PROPOSAL"] || [] // Store the detailed proposals
                }));
                
                setAfrrProposals(flattenedProposals);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setAfrrProposals([]);
                setLoading(false);
            }
        };

        getAfrrProposals();
        getAfrrReservations();
    }, [dateFrom, dateTo]);

    function normalizeDate(dateStr) {
        // Tarih string'ini ISO formatına çevir (zaman dilimi farkı önemsiz olacak şekilde)
        return new Date(dateStr).toISOString(); // örnek: "2025-05-12T22:00:00.000Z"
      }
      
      function mergeDataArrays(data1, data2) {
        const result = [];
        const map = new Map();
      
        for (const item of data1) {
          map.set(item.date, { ...item, details: [...item.details] });
        }
      
        for (const item of data2) {
          if (!map.has(item.date)) {
            map.set(item.date, { ...item, details: [...item.details] });
          } else {
            const existing = map.get(item.date);
      
            // normalize edilmiş "from" date'e göre eşleşme yapılacak
            const detailMap = new Map();
            for (const detail of existing.details) {
              detailMap.set(normalizeDate(detail.from), { ...detail });
            }
      
            for (const detail of item.details) {
              const key = normalizeDate(detail.from);
              if (detailMap.has(key)) {
                const existingDetail = detailMap.get(key);
                // "from" ve "to" değerleri sabit tutulur, diğer tüm key'ler birleştirilir
                detailMap.set(key, {
                  ...existingDetail,
                  ...detail,
                  from: existingDetail.from, // orijinal 'from' değeri korunsun
                  to: existingDetail.to ?? detail.to // 'to' değeri yoksa diğerinden alınır
                });
              } else {
                detailMap.set(key, { ...detail });
              }
            }
      
            existing.details = Array.from(detailMap.values());
            map.set(item.date, existing);
          }
        }
        const merged = Array.from(map.values());
        
        return merged;
      }


    useEffect(() => {

        if (afrrProposals.length > 0 && afrrReservations.length > 0) {
            const mergedData = [...afrrReservations, ...afrrProposals];
            const normalizedData = mergeDataArrays(afrrReservations, afrrProposals);
            //const normalizedData = normalizeData(mergedData);
            setMergedData(normalizedData);
            // Detect missing days vs filter
            try {
                const fromDate = dayjs(dateFrom.format('YYYY-MM-DD'));
                const toDate = dayjs(dateTo.format('YYYY-MM-DD'));
                const days = [];
                let cursor = fromDate.clone();
                while (cursor.isBefore(toDate) || cursor.isSame(toDate, 'day')) {
                    days.push(cursor.format('YYYY-MM-DD'));
                    cursor = cursor.add(1, 'day');
                }
                const present = new Set(normalizedData.map(d => dayjs(d.date).format('YYYY-MM-DD')));
                const missing = days.filter(d => !present.has(d));
            } catch (e) {
                debugLog('Coverage check error', e?.message);
            }
        }
    }, [afrrProposals, afrrReservations]);

    const handleRowClick = (index) => {
        setExpandedRows(prevState => ({
            ...prevState,
            [index]: !prevState[index]
        }));
    };

    const handleDownloadSingle = (reservation) => {
        try {
            const headers = ['From', 'To', 'FSP confirmed', 'Up Capacity Reserved', 'Capacity Volume Down', 'Minimum Capacity Price Down'];
            const data = reservation.details.map(detail => [
                detail.from, 
                detail.to, 
                detail.afrr_down_capacity_reserved || 'N/A', 
                detail.afrr_up_capacity_reserved || 'N/A', 
                detail.capacity_volume_down, 
                detail.min_cap_price_aFRR_down
            ]);
            const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Reservation");
            // Write file
            XLSX.writeFile(wb, `reservation_${reservation.id}.xlsx`);
            triggerAlert("success", "Reservation downloaded successfully");
        } catch (error) {
            console.error('Error downloading single reservation:', error);
            triggerAlert("error", "Error downloading single reservation");
        } finally {
            setLoading(false);
        }

    };

    const handleDownloadAll = () => {
        try {
            const headers = ['ID', 'Date', 'Volume (MW)', 'Price (€/MW)', 'From', 'To', 'FSP confirmed', 'Up Capacity Reserved', 'Capacity Volume Down', 'Minimum Capacity Price Down'];
            
            const data = mergedData.flatMap(reservation =>
                reservation.details.map(detail => [
                    reservation.id,
                    reservation.date,
                    reservation.volume,
                    reservation.price,
                    detail.from,
                    detail.to,
                    detail.afrr_down_capacity_reserved || 'N/A',
                    detail.afrr_up_capacity_reserved || 'N/A',
                    detail.capacity_volume_down,
                    detail.min_cap_price_aFRR_down
                ])
            );

            const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "All Reservations");
            // Write file
            XLSX.writeFile(wb, `all_reservations_${dateFrom}_${dateTo}.xlsx`);
            triggerAlert("success", "All reservations downloaded successfully");
        } catch (error) {
            console.error('Error downloading all reservations:', error);
            triggerAlert("error", "Error downloading all reservations");
        } finally {
            setLoading(false);
        }
    };
    const formatStringDate = (date) => {
        return date.split('-').reverse().join('-');
    }
    const formatDate = (date) => {

        return dayjs(date).format('DD-MM-YYYY HH:mm');
    }
    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" color={colors.grey[100]}>
                    aFRR Report
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="From Date"
                            value={dateFrom}
                            onChange={(newValue) => setDateFrom(newValue)}
                            format='DD/MM/YYYY'
                            sx={{
                                '& .MuiInputBase-root': {
                                    color: colors.grey[100],
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.grey[700]
                                    }
                                }
                            }}
                        />
                        <DatePicker
                            label="To Date"
                            value={dateTo}
                            onChange={(newValue) => setDateTo(newValue)}
                            format='DD/MM/YYYY'
                            sx={{
                                '& .MuiInputBase-root': {
                                    color: colors.grey[100],
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.grey[700]
                                    }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Box>

                <Button
                    variant="contained"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadAll}
                    sx={{
                        bgcolor: colors.entras_purple[100],
                        '&:hover': {
                            bgcolor: colors.entras_purple[200]
                        }
                    }}
                >
                    Download All
                </Button>
            </Box>
            <Card sx={{ bgcolor: colors.primary[400], boxShadow: 3 }}>


                <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold", width: "10%" }} />
                                <TableCell sx={{ fontWeight: "bold", width: "50%" }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: "30%" }} align='center'>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {mergedData.map((reservation, index) => (
                                <React.Fragment key={index}>
                                    <TableRow>
                                        <TableCell>
                                            <IconButton
                                                aria-label="expand row"
                                                size="small"
                                                onClick={() => handleRowClick(index)}
                                            >
                                                {expandedRows[index] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>AFRR REPORT {formatStringDate(reservation.date)}</TableCell>
                                        <TableCell >{formatStringDate(reservation.date)}</TableCell>
                                        <TableCell align='center'>
                                            <Tooltip title="Download Row">
                                                <IconButton
                                                    onClick={() => handleDownloadSingle(reservation)}
                                                    sx={{ color: colors.grey[100] }}
                                                >
                                                    <FileDownloadIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>

                                    </TableRow>

                                    <TableRow>

                                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>

                                            <Collapse in={expandedRows[index]} timeout="auto" unmountOnExit>

                                                <Table size="small" aria-label="purchases">

                                                    <TableHead>

                                                        <TableRow sx={{}}>
                                                            <TableCell width="10%"></TableCell>

                                                            <TableCell width="20%" >From</TableCell>

                                                            <TableCell width="20%" >To</TableCell>

                                                            <TableCell width="20%"  >aFRR down capacity reserved (kW) - FSP confirmed</TableCell>
                                                            <TableCell width="20%" >aFRR down capacity proposal (kW) - Endustries proposal</TableCell>
                                                            <TableCell width="20%" >aFRR down minimum capacity price (€/MW/h) - Endustries proposal</TableCell>


                                                            {/*                                                             <TableCell>Up Capacity Reserved</TableCell>
 */}
                                                        </TableRow>

                                                    </TableHead>

                                                    <TableBody sx={{}}>

                                                        {reservation.details.map((detail, detailIndex) => (
                                                            <TableRow key={detailIndex} sx={{}}>
                                                                <TableCell width="10%" ></TableCell>
                                                                <TableCell width="20%" >{formatDate(detail.from)}</TableCell>
                                                                <TableCell width="20%" >{formatDate(detail.to)}</TableCell>
                                                                <TableCell width="20%" >{detail.afrr_down_capacity_reserved || 'N/A'}</TableCell>
                                                                <TableCell width="20%" >{detail.capacity_volume_down}</TableCell>
                                                                <TableCell width="20%" >{detail.min_cap_price_aFRR_down}</TableCell>


                                                                {/*                                                                 <TableCell>{detail.afrr_up_capacity_reserved}</TableCell>
 */}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>

                                                </Table>

                                            </Collapse>

                                        </TableCell>

                                    </TableRow>

                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </>
    );

};



export default AfrrReservation;
